/**
 * 4-Lock skins add-on — see snippets/bundle-skins.liquid and kit SKINS.md.
 *
 * Lets a compatible skin ride along as its own cart line instead of the ski carrying a
 * "+ skins" variant. Three jobs:
 *
 *   1. Re-render the skin options when the shopper changes ski length (skin length follows
 *      ski length, so the compatible skin variant changes with it).
 *   2. Keep the price displays honest: with a skin selected they show ski + discounted skin
 *      (the ski itself only discounts alongside a BINDING, so the teaser stays visible as
 *      the pointer to that better price — and its price widens to the ski + skin pair at
 *      the percentage off while a skin is selected, since a binding discounts all three).
 *   3. Turn the main product form into a two-item add-to-cart, and ride along on a
 *      bindings card's add-both as a third item (ski + binding + skin).
 *
 * On (3): the theme's own handler is a document-delegated *bubble* listener that does
 * `new FormData(form)` and POSTs it to /cart/add.js (theme.dev.js ~10072). So renaming the
 * form's fields to `items[N][...]` in the *capture* phase gets a multi-item add for free,
 * along with the theme's loading state, error placement and cart drawer refresh. The form is
 * restored on `theme:cart:add` (with a setTimeout backstop) — NEVER in a microtask: for a
 * TRUSTED click (a real mouse, CDP input) the JS stack empties after each listener, so a
 * microtask checkpoint runs BETWEEN this capture listener and the theme's bubble listener —
 * a microtask restore strips the payload before the theme ever reads the form, and the
 * shopper silently gets a single-item add. Synthetic clicks (element.click() in tests) defer
 * microtasks to the end of the dispatch, which is how that bug passed scripted QA while
 * failing real mice. See scheduleRestore().
 *
 * Verified against the live store: `items[]` coexists fine with the theme's stray
 * `options[Size]` inputs. Skins need no bundle property: the automatic discount uses
 * the merchandise in the cart. An add-on selection does not mean that discount wins
 * at checkout (for example, FRNTLINE pricing may apply instead).
 *
 * Fully event-delegated with no per-block initialisation, so blocks that appear later
 * (quick view, section re-render) work without re-running anything.
 */
(function () {
  'use strict';

  var SELECTORS = {
    block: '[data-bundle-skins]',
    buttons: '[data-bundle-skins-buttons]',
    input: '[data-bundle-skins-input]',
    map: '[data-bundle-skins-map]',
    productSelect: '[data-product-select]',
    cardSkiId: '[data-bundle-ski-id]',
    quantity: '[name="quantity"]',
    addToCart: '[data-add-to-cart]',
    teaserPrice: '[data-bundle-teaser] .bundle-price-teaser__price',
    section: '[data-section-id]',
    optionButtons: '[data-variant-buttons]',
    submitHolder: '.product__submit__holder',
  };

  var CLASSES = {
    hidden: 'bundle-skins--hidden',
  };

  // ---------------------------------------------------------------- helpers

  function moneyFormat(block) {
    var themeFormat = window.theme && window.theme.moneyFormat;
    return themeFormat || block.getAttribute('data-bundle-skins-money-format') || '${{amount}}';
  }

  /**
   * Whole-dollar amounts drop the trailing .00, matching the Liquid render of the toggle
   * labels. `keepCents` keeps them — the price displays always show cents ($1,079.00).
   */
  function formatMoney(block, cents, keepCents) {
    var decimals = !keepCents && cents % 100 === 0 ? 0 : 2;
    var parts = (cents / 100).toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat(block).replace(/\{\{\s*amount[^}]*\}\}/, parts.join('.'));
  }

  function readMap(block) {
    var node = block.querySelector(SELECTORS.map);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function intAttr(el, name) {
    return parseInt(el.getAttribute(name), 10) || 0;
  }

  /** The bundle percentage — parseFloat, so "10.0" (or a future 7.5) survives intact. */
  function pctOf(block) {
    return parseFloat(block.getAttribute('data-bundle-skins-pct')) || 0;
  }

  /** What the cart will charge for one line: the bundle percentage off, rounded per line. */
  function discounted(cents, pct) {
    return Math.round((cents * (100 - pct)) / 100);
  }

  /**
   * First match that this form actually owns.
   *
   * The bindings block is DOM-inserted into the product form, and its cards carry their own
   * `<form>` elements — legal via the DOM, though the HTML parser would forbid it. So a plain
   * `form.querySelector()` can reach into a nested card form and return the wrong input.
   */
  function ownedBy(form, selector) {
    var candidates = form.querySelectorAll(selector);
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].closest('form') === form) return candidates[i];
    }
    return null;
  }

  function selectedInput(block) {
    var inputs = block.querySelectorAll(SELECTORS.input);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked && inputs[i].value) return inputs[i];
    }
    return null;
  }

  /** Index of the checked skin among the skin options, 0 meaning "No skins". */
  function selectedIndex(block) {
    var inputs = block.querySelectorAll(SELECTORS.input);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) return i;
    }
    return 0;
  }

  // ---------------------------------------------------------------- render

  function renderOptions(block, skiVariantId) {
    var wrap = block.querySelector(SELECTORS.buttons);
    if (!wrap) return;

    var skins = readMap(block)[String(skiVariantId)] || [];
    var blockId = block.getAttribute('data-bundle-skins-block') || '';
    var pct = pctOf(block);
    var noneLabel = block.getAttribute('data-bundle-skins-none-label') || 'No skins';
    // Preserve the shopper's intent across a size change: if they wanted skins, keep the
    // option in the same position selected, provided it still exists and is in stock.
    var keepIndex = selectedIndex(block);

    var html =
      '<span class="radio__button" style="position: relative;">' +
      '<input type="radio" class="radio__input" name="bundle_skins_choice--' +
      blockId +
      '" id="BundleSkins--' +
      blockId +
      '--none" value="" data-bundle-skins-input>' +
      '<label for="BundleSkins--' +
      blockId +
      '--none" class="radio__label"><span class="option-title"></span></label>' +
      '</span>';

    for (var i = 0; i < skins.length; i++) {
      var skin = skins[i];
      html +=
        '<span class="radio__button" style="position: relative;">' +
        '<input type="radio" class="radio__input" name="bundle_skins_choice--' +
        blockId +
        '" id="BundleSkins--' +
        blockId +
        '--' +
        skin.id +
        '" value="' +
        skin.id +
        '" data-bundle-skins-input data-bundle-skins-price="' +
        skin.price +
        '"' +
        (skin.available ? '' : ' disabled') +
        '>' +
        '<label for="BundleSkins--' +
        blockId +
        '--' +
        skin.id +
        '" class="radio__label"><span class="option-title"></span></label>' +
        '</span>';
    }

    wrap.innerHTML = html;

    // Labels are filled as text, never as markup, so a product title can't inject HTML.
    var titles = wrap.querySelectorAll('.option-title');
    titles[0].textContent = noneLabel;
    for (var j = 0; j < skins.length; j++) {
      var title = titles[j + 1];
      if (!title) break;
      title.appendChild(document.createTextNode('+ ' + skins[j].label + ' '));
      if (skins[j].available) {
        var net2 = discounted(skins[j].price, pct);
        var price = document.createElement('span');
        price.className = 'bundle-skins__price';
        price.textContent = formatMoney(block, net2);
        title.appendChild(price);
        var was = document.createElement('s');
        was.className = 'bundle-skins__was';
        was.textContent = formatMoney(block, skins[j].price);
        title.appendChild(was);
      } else {
        var oos = document.createElement('span');
        oos.className = 'bundle-skins__oos';
        oos.textContent = 'Sold out';
        title.appendChild(oos);
      }
    }

    var inputs = wrap.querySelectorAll(SELECTORS.input);
    var restore = inputs[keepIndex];
    if (keepIndex > 0 && restore && !restore.disabled) {
      restore.checked = true;
    } else {
      inputs[0].checked = true;
    }
  }

  /**
   * With a skin selected, the section's price displays — the main price and the price inside
   * the Add to Cart button, both marked [data-product-price] — show the combined charge:
   * full ski + percentage-off skin. The ski itself only discounts when a BINDING joins the
   * cart (the teaser advertises that), so it stays full here. The theme
   * rewrites those elements itself on every variant change and dispatches
   * `theme:variant:change` afterwards, so re-running this on that event keeps the override
   * on top; deselecting restores the plain ski price the same way the theme writes it.
   */
  function syncPrices(block) {
    var section = block.closest(SELECTORS.section);
    var els = section ? section.querySelectorAll('[data-product-price]') : [];
    if (!els.length) return;

    var ski = intAttr(block, 'data-bundle-skins-ski-price');
    if (!ski) return;

    var input = selectedInput(block);
    var text;
    if (input && !block.classList.contains(CLASSES.hidden)) {
      var skin = intAttr(input, 'data-bundle-skins-price');
      text = formatMoney(block, ski + discounted(skin, pctOf(block)), true);
      block.dataset.bundleSkinsPriced = '1';
    } else if (block.dataset.bundleSkinsPriced) {
      // Only write the plain ski price back if the override is ours to undo.
      text = formatMoney(block, ski, true);
      delete block.dataset.bundleSkinsPriced;
    } else {
      return;
    }

    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }

  /**
   * The bindings cards' receipts list what their button adds — with a skin selected that's
   * binding + ski + skin, all at the bundle percentage off, so each card gets its skin row
   * revealed and its "Bundle total" recomputed as pct-off (binding + ski, the bindings
   * script's own formula) plus the pct-off skin. The bindings block's own
   * sync script rewrites the two-item totals on every variant change and was registered
   * before this deferred script, so re-running here (same event, later listener) keeps the
   * three-item totals on top. Restores only totals this script overrode.
   */
  function syncCards(block) {
    var section = block.closest(SELECTORS.section);
    var cards = section ? section.querySelectorAll('[data-bundle-binding-cents]') : [];
    if (!cards.length) return;

    var ski = intAttr(block, 'data-bundle-skins-ski-price');
    if (!ski) return;

    var input = selectedInput(block);
    var active = !!input && !block.classList.contains(CLASSES.hidden);
    if (!active && !block.dataset.bundleSkinsCarded) return;

    var skinPrice = 0;
    var skinLabel = '';
    if (active) {
      skinPrice = intAttr(input, 'data-bundle-skins-price');
      var select = section.querySelector(SELECTORS.productSelect);
      var skins = readMap(block)[select ? String(select.value) : ''] || [];
      for (var s = 0; s < skins.length; s++) {
        if (String(skins[s].id) === input.value) {
          skinLabel = skins[s].label;
          break;
        }
      }
    }
    var skinNet = discounted(skinPrice, pctOf(block));
    var rowToggled = false;

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var row = card.querySelector('[data-bundle-skin-row]');
      if (row && row.hidden === active) {
        row.hidden = !active;
        rowToggled = true;
      }
      if (row && active) {
        row.querySelector('[data-bundle-skin-name]').textContent = skinLabel;
        row.querySelector('[data-bundle-skin-price]').textContent = formatMoney(block, card.hasAttribute('data-bundle-ski-only') ? skinNet : skinPrice, true);
      }

      var newEl = card.querySelector('[data-bundle-total-new]');
      var oldEl = card.querySelector('[data-bundle-total-old]');
      if (newEl && oldEl) {
        var pct = intAttr(card, 'data-bundle-pct');
        var pairFull = intAttr(card, 'data-bundle-binding-cents') + ski;
        var pairNew = card.hasAttribute('data-bundle-ski-only') ? intAttr(card, 'data-bundle-binding-cents') + discounted(ski, pct) : Math.round((pairFull * (100 - pct)) / 100);
        newEl.textContent = formatMoney(block, pairNew + skinNet, true);
        oldEl.textContent = formatMoney(block, pairFull + skinPrice, true);
        oldEl.hidden = pairNew + skinNet === pairFull + skinPrice;
      }
    }

    if (active) {
      block.dataset.bundleSkinsCarded = '1';
    } else {
      delete block.dataset.bundleSkinsCarded;
    }
    // Flickity measures cell heights; renudge when a row appears or disappears.
    if (rowToggled) window.dispatchEvent(new Event('resize'));
  }

  /**
   * Joy's points calculator, on Online Store themes, prices from ITS OWN catalog copy:
   * `window.AVADA_JOY.product.variants[].price` (cents), resolved by the current variant id,
   * then recomputes on the global `avadaJoyRerenderCalculator()`. (Its documented
   * `#section-price [data-joy-product-price]` DOM hook is dead here — that branch only runs
   * on hydrogen storefronts; verified in their DisplayManager bundle.) So: set the selected
   * variant's in-memory price to the effective total and re-render; deselecting writes the
   * plain ski price back the same way. Joy awards points server-side on the amount actually
   * paid, so this is display parity, not a points change.
   */
  function syncJoy(block) {
    var joy = window.AVADA_JOY;
    if (!joy || !joy.product || !joy.product.variants) return;

    var section = block.closest(SELECTORS.section);
    var select = section && section.querySelector(SELECTORS.productSelect);
    var id = select && parseInt(select.value, 10);
    if (!id) return;

    var variant = null;
    for (var i = 0; i < joy.product.variants.length; i++) {
      if (joy.product.variants[i].id === id) {
        variant = joy.product.variants[i];
        break;
      }
    }
    // No match means this block belongs to some other product (a featured-product section),
    // not the page whose calculator Joy is showing.
    if (!variant) return;

    var ski = intAttr(block, 'data-bundle-skins-ski-price');
    if (!ski) return;

    var input = selectedInput(block);
    var cents = ski;
    if (input && !block.classList.contains(CLASSES.hidden)) {
      cents = ski + discounted(intAttr(input, 'data-bundle-skins-price'), pctOf(block));
    }
    if (variant.price === cents) return;

    // Mutate even when the re-render API hasn't loaded yet: Joy reads this value when it
    // initializes, so an early skin selection still comes up right.
    variant.price = cents;
    if (typeof window.avadaJoyRerenderCalculator === 'function') {
      window.avadaJoyRerenderCalculator();
    }
  }

  /**
   * The teaser advertises what the bundle-eligible lines will cost once a binding joins:
   * the ski alone at the percentage off (the Liquid-rendered value), or — while a skin is
   * selected — the ski + skin pair, both at the percentage off, rounded per line the way the
   * discount engine rounds. Skis without a skins block never load this script, so their
   * static Liquid teaser stands untouched.
   */
  function syncTeaser(block) {
    var section = block.closest(SELECTORS.section);
    var priceEl = section && section.querySelector(SELECTORS.teaserPrice);
    if (!priceEl) return;

    var ski = intAttr(block, 'data-bundle-skins-ski-price');
    if (!ski) return;

    var pct = pctOf(block);
    var skiPct = block.hasAttribute('data-bundle-ski-pct') ? intAttr(block, 'data-bundle-ski-pct') : pct;
    var input = selectedInput(block);
    var text;
    if (input && !block.classList.contains(CLASSES.hidden)) {
      var skin = intAttr(input, 'data-bundle-skins-price');
      text = formatMoney(block, discounted(ski, skiPct) + discounted(skin, pct), true);
      block.dataset.bundleSkinsTeased = '1';
    } else if (block.dataset.bundleSkinsTeased) {
      // Only write the plain discounted-ski price back if the override is ours to undo.
      text = formatMoney(block, discounted(ski, skiPct), true);
      delete block.dataset.bundleSkinsTeased;
    } else {
      return;
    }
    priceEl.textContent = text;
  }

  function refresh(block) {
    syncPrices(block);
    syncTeaser(block);
    syncCards(block);
    syncJoy(block);
  }

  // ---------------------------------------------------------------- placement

  /**
   * Blocks render outside the product `<form>` — Liquid can't emit them inside it, because
   * the cards in neighbouring blocks carry their own `<form>` and the HTML parser forbids
   * nesting those. The DOM has no such rule, so move it in here.
   *
   * Anchored after the last variant-option group, *not* before the ATC holder: the bindings
   * block claims that anchor, and sharing it would make the final order depend on which
   * script ran last. Result: size picker -> skins -> bindings -> ATC.
   */
  function place(block) {
    if (block.dataset.bundleSkinsPlaced) return;
    var section = block.closest(SELECTORS.section);
    if (!section) return;

    // Prefer the wrapper's form, but fall back to whichever form in the section owns the variant
    // input — that is the product form regardless of wrapper markup.
    var form = section.querySelector('[data-form-wrapper] form');
    if (!form) {
      var forms = section.querySelectorAll('form');
      for (var f = 0; f < forms.length && !form; f++) {
        if (ownedBy(forms[f], SELECTORS.productSelect)) form = forms[f];
      }
    }
    // Not marked as placed: a later pass can retry rather than silently giving up forever.
    if (!form) return;
    if (form.contains(block)) {
      block.dataset.bundleSkinsPlaced = '1';
      return;
    }

    // Last VISIBLE option group: the retired 4-Lock option (and the sibling-hide feature)
    // keep their group in the DOM inside a `.hidden` selector-wrapper for variant
    // resolution — anchoring in there would swallow this block into display:none.
    var groups = form.querySelectorAll(SELECTORS.optionButtons);
    var anchor = null;
    for (var g = groups.length - 1; g >= 0 && !anchor; g--) {
      var wrapper = groups[g].closest('[data-selector-wrapper]');
      if (wrapper && wrapper.classList.contains('hidden')) continue;
      anchor = groups[g].closest('fieldset');
    }

    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(block, anchor.nextSibling);
    } else {
      var holder = form.querySelector(SELECTORS.submitHolder);
      if (holder && holder.parentNode) holder.parentNode.insertBefore(block, holder);
    }
    block.dataset.bundleSkinsPlaced = '1';
  }

  function placeAll() {
    var blocks = document.querySelectorAll(SELECTORS.block);
    for (var i = 0; i < blocks.length; i++) {
      place(blocks[i]);
      refresh(blocks[i]);
    }
  }

  // ---------------------------------------------------------------- events

  document.addEventListener('change', function (event) {
    var input = event.target.closest && event.target.closest(SELECTORS.input);
    if (!input) return;
    var block = input.closest(SELECTORS.block);
    if (block) refresh(block);
  });

  /**
   * The theme dispatches this on the outer product `<section>` — an *ancestor* of the block —
   * so it never reaches a listener bound inside. Listen at document level and filter.
   */
  document.addEventListener('theme:variant:change', function (event) {
    var blocks = document.querySelectorAll(SELECTORS.block);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (!event.target || !event.target.contains || !event.target.contains(block)) continue;

      var variant = event.detail && event.detail.variant;
      var available = !!(variant && variant.available);

      // A sold-out or nonexistent ski has no sellable add-on: hide the whole control and let
      // the main ATC's "Sold Out — Notify Me" own that story.
      block.classList.toggle(CLASSES.hidden, !available);

      if (variant) {
        block.setAttribute('data-bundle-skins-ski-price', variant.price);
        if (block.hasAttribute('data-bundle-ski-pct')) block.setAttribute('data-bundle-ski-pct', variant.compare_at_price > variant.price ? 0 : 10);
        renderOptions(block, variant.id);
      }
      refresh(block);
    }
  });

  /**
   * The skins block that belongs to this form's product. Scoped to the section so it works
   * whether or not the block was moved into the form. If the block sits outside the section
   * too, fall back to any block whose map knows this ski variant — that proves it belongs to
   * this product, so a second product's block (quick view, a featured-product section) can
   * never be picked up by mistake.
   */
  function resolveBlock(form, skiVariantId) {
    var scope = form.closest(SELECTORS.section);
    var block = scope && scope.querySelector(SELECTORS.block);
    if (!block) {
      var candidates = document.querySelectorAll(SELECTORS.block);
      for (var c = 0; c < candidates.length; c++) {
        if (readMap(candidates[c])[String(skiVariantId)]) {
          block = candidates[c];
          break;
        }
      }
    }
    return block;
  }

  /**
   * The skin the shopper picked for this ski variant, or null when "No skins" is selected,
   * the block is hidden, or no block belongs to this form. A selected-but-disabled skin
   * cancels the add outright (belt and braces: never POST a pair whose skin side is gone,
   * even if the disabled state failed to render).
   */
  function chosenSkin(event, form, skiVariantId) {
    var block = resolveBlock(form, skiVariantId);
    if (!block || block.classList.contains(CLASSES.hidden)) return null;

    var input = selectedInput(block);
    if (!input) return null;

    if (input.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return null;
    }
    return input;
  }

  function injectHidden(form, injected, name, value) {
    var el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    el.value = value;
    form.appendChild(el);
    injected.push(el);
  }

  /**
   * Restores the form once the theme's bubble listener has read it. NEVER a microtask: on
   * trusted clicks a microtask checkpoint runs between the capture and bubble listeners and
   * would strip the payload before the theme serializes the form (see the header comment).
   *
   * Primary trigger is `theme:cart:add`, which the theme dispatches synchronously right
   * after it has built its FormData (theme.dev.js ~10120) — FormData copies values at
   * construction, so restoring the DOM then cannot touch the request. The setTimeout is a
   * backstop for paths that never reach the theme's handler (disabled button, sold-out
   * guard's stopPropagation); it can be delayed by hidden-tab timer throttling, during
   * which the form sits unused anyway.
   */
  function scheduleRestore(restore) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      document.removeEventListener('theme:cart:add', run, true);
      restore();
    }
    document.addEventListener('theme:cart:add', run, true);
    setTimeout(run, 0);
  }

  function removeInjected(injected) {
    for (var i = 0; i < injected.length; i++) {
      if (injected[i].parentNode) injected[i].parentNode.removeChild(injected[i]);
    }
  }

  /**
   * Capture phase, so this lands before the theme's bubble-phase ATC handler builds its
   * FormData. Two add-to-cart flavours carry the skin:
   *
   *   - the main product form (owns [data-product-select]): its own fields are renamed to
   *     items[0][...] and the skin injected as items[1];
   *   - a bindings card's nested form (owns [data-bundle-ski-id]): it already posts
   *     items[0] binding + items[1] ski, so the skin rides along as items[2].
   *
   * Upsell cards and any other form fall through untouched. Both forms are identified by an
   * input they OWN, never by where the skins block happens to sit — relocation into the form
   * is cosmetic and can be skipped (unexpected markup, a re-render).
   */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest(SELECTORS.addToCart);
      if (!button) return;

      var form = button.closest('form');
      if (!form) {
        return;
      }

      var idInput = ownedBy(form, SELECTORS.productSelect);
      if (idInput) {
        var block = chosenSkin(event, form, idInput.value);
        if (!block) return; // "No skins" — leave the single-item path completely alone.

        var qtyInput = ownedBy(form, SELECTORS.quantity);
        var qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;

        var originalIdName = idInput.getAttribute('name');
        var originalQtyName = qtyInput ? qtyInput.getAttribute('name') : null;

        idInput.setAttribute('name', 'items[0][id]');
        if (qtyInput) qtyInput.setAttribute('name', 'items[0][quantity]');

        var injected = [];
        if (!qtyInput) injectHidden(form, injected, 'items[0][quantity]', qty);
        injectHidden(form, injected, 'items[1][id]', block.value);
        injectHidden(form, injected, 'items[1][quantity]', qty);

        scheduleRestore(function () {
          idInput.setAttribute('name', originalIdName);
          if (qtyInput && originalQtyName) qtyInput.setAttribute('name', originalQtyName);
          removeInjected(injected);
        });
        return;
      }

      var cardSkiInput = ownedBy(form, SELECTORS.cardSkiId);
      if (cardSkiInput) {
        // The section's variant input is the authoritative ski id: the card's own input is
        // synced FROM it by the bindings script, whose capture listener sits deeper in the
        // tree and therefore runs after this one.
        var section = form.closest(SELECTORS.section);
        var select = section && section.querySelector(SELECTORS.productSelect);
        var skiId = (select && select.value) || cardSkiInput.value;

        var skin = chosenSkin(event, form, skiId);
        if (!skin) return; // "No skins" — the card's own two-item add stands.

        // The card posts one binding and one ski regardless of the main form's quantity
        // stepper, so the skin matches at quantity 1.
        var cardInjected = [];
        injectHidden(form, cardInjected, 'items[2][id]', skin.value);
        injectHidden(form, cardInjected, 'items[2][quantity]', 1);
        scheduleRestore(function () {
          removeInjected(cardInjected);
        });
        return;
      }

    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeAll);
  } else {
    placeAll();
  }

  // Quick view and other AJAX-rendered sections mount after load.
  document.addEventListener('theme:cart:add', placeAll);
  document.addEventListener('shopify:section:load', placeAll);
})();
